import { FileQuestion } from 'lucide-react';

/**
 * EmptyState Component - Display when no data is available
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.icon - Custom icon (default: FileQuestion)
 * @param {string} props.title - Title text
 * @param {string} props.description - Description text
 * @param {React.ReactNode} props.action - Action button or element
 * @param {string} props.size - Size: 'sm', 'md', 'lg'
 * @param {string} props.className - Additional CSS classes
 */
const EmptyState = ({
  icon: Icon = FileQuestion,
  title = 'No hay datos',
  description = 'No se encontraron resultados para mostrar.',
  action,
  size = 'md',
  className = '',
}) => {
  // Size classes
  const sizeClasses = {
    sm: {
      container: 'py-6',
      icon: 'w-12 h-12',
      title: 'text-base',
      description: 'text-sm',
    },
    md: {
      container: 'py-12',
      icon: 'w-16 h-16',
      title: 'text-lg',
      description: 'text-sm',
    },
    lg: {
      container: 'py-20',
      icon: 'w-20 h-20',
      title: 'text-xl',
      description: 'text-base',
    },
  };

  const sizes = sizeClasses[size];

  return (
    <div className={`text-center ${sizes.container} ${className}`}>
      <div className="flex justify-center">
        <Icon className={`${sizes.icon} text-gray-300`} />
      </div>
      <h3 className={`mt-4 font-medium text-gray-900 ${sizes.title}`}>
        {title}
      </h3>
      {description && (
        <p className={`mt-2 text-gray-500 max-w-md mx-auto ${sizes.description}`}>
          {description}
        </p>
      )}
      {action && (
        <div className="mt-6">
          {action}
        </div>
      )}
    </div>
  );
};

/**
 * EmptyStateVariants - Pre-built empty states for common scenarios
 */
export const NoResultsState = ({ onClear, className = '' }) => (
  <EmptyState
    icon={FileQuestion}
    title="Sin resultados"
    description="No se encontraron resultados con los filtros actuales. Intenta ajustar los criterios de búsqueda."
    action={
      onClear && (
        <button onClick={onClear} className="btn btn-primary">
          Limpiar filtros
        </button>
      )
    }
    className={className}
  />
);

export const NoDataState = ({ 
  title = "No hay datos",
  description = "Aún no hay datos para mostrar.",
  action,
  className = '' 
}) => (
  <EmptyState
    title={title}
    description={description}
    action={action}
    className={className}
  />
);

export const ErrorState = ({ 
  title = "Error al cargar",
  description = "Ocurrió un error al cargar los datos. Por favor intenta de nuevo.",
  onRetry,
  className = '' 
}) => (
  <EmptyState
    icon={FileQuestion}
    title={title}
    description={description}
    action={
      onRetry && (
        <button onClick={onRetry} className="btn btn-primary">
          Reintentar
        </button>
      )
    }
    className={className}
  />
);

export default EmptyState;